import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import api from "@/lib/api";

const SNOOZE_DAYS = 14;

function alreadyAsked(type, entityKey) {
  try {
    if (localStorage.getItem(`or_fb_done:${type}:${entityKey}`)) return true;
    const ts = Number(localStorage.getItem(`or_fb_snooze:${type}`) || 0);
    return Date.now() - ts < SNOOZE_DAYS * 86400000;
  } catch {
    return false;
  }
}

function markAsked(type, entityKey) {
  try {
    localStorage.setItem(`or_fb_done:${type}:${entityKey}`, "1");
    localStorage.setItem(`or_fb_snooze:${type}`, String(Date.now()));
  } catch {
    /* storage unavailable - fine, prompt may reappear */
  }
}

/**
 * One-question inline feedback card. Shows at most once per entity and
 * snoozes its type for 14 days after any answer/dismiss.
 * type: contextual_interview_helpful | contextual_report_helpful | contextual_slow_wait
 */
export const ContextualFeedback = ({ type, entityKey, question, options, context }) => {
  const location = useLocation();
  const [hidden, setHidden] = useState(() => alreadyAsked(type, entityKey));
  const [answered, setAnswered] = useState(false);

  if (hidden) return null;

  const answer = async (value) => {
    setAnswered(true);
    markAsked(type, entityKey);
    try {
      await api.post("/feedback", {
        category: type,
        text: value,
        page: location.pathname,
        context: { ...(context || {}), entity: entityKey },
      });
    } catch {
      /* best-effort - never block the user */
    }
    setTimeout(() => setHidden(true), 1600);
  };

  const dismiss = () => {
    markAsked(type, entityKey);
    setHidden(true);
  };

  return (
    <div
      data-testid={`ctx-feedback-${type}`}
      className="rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-3 flex items-center gap-3 flex-wrap"
    >
      {answered ? (
        <div className="text-xs text-emerald-300" data-testid="ctx-feedback-thanks">
          Thanks, your feedback helps us improve Intuiterview.
        </div>
      ) : (
        <>
          <div className="text-xs text-zinc-300 flex-1 min-w-[200px]">{question}</div>
          <div className="flex items-center gap-1.5">
            {options.map((o) => (
              <button
                key={o.value}
                type="button"
                data-testid={`ctx-feedback-opt-${o.value}`}
                onClick={() => answer(o.value)}
                className="text-xs rounded-full border border-zinc-700 text-zinc-300 hover:border-amber-500/50 hover:text-amber-300 hover:bg-amber-500/[0.06] px-3 py-1.5 transition-colors"
              >
                {o.label}
              </button>
            ))}
            <button
              type="button"
              data-testid="ctx-feedback-dismiss"
              onClick={dismiss}
              aria-label="Dismiss"
              className="p-1 rounded-md text-zinc-600 hover:text-zinc-300"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ContextualFeedback;
